/* eslint-disable no-bitwise,no-plusplus */
import * as authModules from "./const/authModules";

/**
 * The company/site ID that signifies global permission
 * @type {string}
 */
export const rootId = "*";

/**
 * The set of available user rights
 * @type {string[]}
 */
export const claims = [
  "view-role",
  "edit-role",
  "create-user",
  "delete-user",
  "assign-user-role",
  "edit-setting",
  "view-schedule",
  "edit-schedule",
  "view-product",
  "edit-product",
  "view-line",
  "edit-line",
  "assign-line-printer",
  "view-downtimeReason",
  "edit-downtimeReason",
  "view-siteDef",
  "edit-siteDef",
  "edit-siteConfig",
  "view-report", // view reports (run oee, downtimes, etc) and save local reports and schedule local reports
  "edit-global-report", // save global reports and schedule global reports
  "assign-line-product",
  "assign-downtime-reason",
  "apply-qa",
  "edit-design",
  "send-printerJob", // send job to printer using RES
  "download-printerJob", // save to usb
  "view-inspection",
  "edit-inspection",
  "perform-inspection",
  "edit-assessment",
  "view-assessment",
  "approve-assessment",
  "edit-printerJob", // configure printer jobs
  "execute-printerJob", // execute printer jobs
  "view-deviceRole", // view device-roles
  "edit-deviceRole", // edit device-roles
];

export const vrsAdmin = "Administrator";
export const vrsMaintenance = "Maintenance";
export const vrsUser = "User";
export const vrsSiteExpireHours = 48;
export const apiFullAccess = "full-access";
export const apiFieldServiceAccess = "fieldService-access";

export const vrsOperationsCompanyId = "Videojet"; // We use this company for videojet global access
export const vrsOperationsSiteId = "VrsInternal"; // We use this site id for videojet global access
export const vrsOperationsContractAdmin = "Videojet-ContractAdmin";
export const vrsOperationsService = "Videojet-Service";
export const vrsOperationsVideojet = "Videojet";
export const vrsOperationsCSE = "Videojet-CSE";
export const vrsOperationsSalesUser = "Videojet-SalesUser";

// Reporting Permissions
export const PrinterPerformance = "PrinterPerformance";
export const ActiveConnectedReport = "ActiveConnectedReport";
export const PrinterSnapshotReport = "PrinterSnapshotReport";
export const RapidRecoverReport = "RapidRecoverReport";
export const VRSUsageReport = "VRSUsageReport";
export const PlantLocationReport = "PlantLocationReport";
export const WFCBillingReport = "WFCBillingReport";
export const WFCInventoryReport = "WFCInventoryReport";
export const CellularConnectivityReport = "CellularConnectivityReport";
export const UnitsRequiringAttentionReport = "UnitsRequiringAttentionReport";
export const ConsumablesReport = "ConsumablesReport";
export const Enabled = "Enabled";

export const authModuleClaims = {
  vrs: [vrsAdmin, vrsMaintenance, vrsUser],
  vrsOperations: [
    vrsOperationsContractAdmin,
    vrsOperationsService,
    vrsOperationsVideojet,
    vrsOperationsCSE,
    vrsOperationsSalesUser,
  ],
  vrsReporting: [
    PrinterPerformance,
    ActiveConnectedReport,
    PrinterSnapshotReport,
    RapidRecoverReport,
    VRSUsageReport,
    PlantLocationReport,
    WFCBillingReport,
    WFCInventoryReport,
    CellularConnectivityReport,
    UnitsRequiringAttentionReport,
    ConsumablesReport,
  ],
  vrsSuperUser: [Enabled],
  vccm: claims,
  api: [apiFullAccess, apiFieldServiceAccess],
};

/**
 * Map license modules to auth modules
 * @type {{vrs: string}}
 */
export const siteForeignModuleAuthMap = {
  vrs: authModules.VRS_AUTH_MODULE,
  vrsOperations: authModules.VRS_OPERATIONS_AUTH_MODULE,
  vrsReporting: authModules.VRS_REPORTING_AUTH_MODULE,
  vrsSuperUser: authModules.VRS_SUPERUSER_AUTH_MODULE,
  api: authModules.API_AUTH_MODULE,
};

export const vrsRoles = {
  site: [vrsAdmin, vrsMaintenance, vrsUser],
  company: [],
  siteDefault: vrsUser,
  admin: vrsAdmin,
};
export const apiRoles = {
  site: [apiFullAccess],
  company: [],
  special: [apiFieldServiceAccess],
  siteDefault: apiFullAccess,
  admin: apiFullAccess,
};

export const vrsOperationsRoles = {
  site: [
    vrsOperationsContractAdmin,
    vrsOperationsService,
    vrsOperationsVideojet,
    vrsOperationsCSE,
    vrsOperationsSalesUser,
  ],
  company: [],
  siteDefault: vrsOperationsCSE,
  admin: vrsOperationsVideojet,
};

export const systemUpdateUser = "system@videojet.com";

/**
 * Split a ID list into an array
 * @param {string} list
 * @returns {string[]}
 */
export function splitIds(list) {
  return (list || "").split("|").filter((s) => s);
}

/**
 * Get the "basic access" claims
 * @returns {string[]}
 */
export function defaultAccessClaims() {
  return claims.filter((c) => c.startsWith("view-"));
}

/**
 * For a site, get auth modules from license modules
 * @param site
 * @returns {*[]}
 */
export const foreignAuthModulesForSite = (site) =>
  (
    (Array.isArray(site && site.modules)
      ? site.modules
      : site && site.modules && site.modules.values) || []
  )
    .map((moduleName) => siteForeignModuleAuthMap[moduleName])
    .filter((authMod) => authMod);

export const siteHasForeignAuthModule = (site, foreignAuthModule) =>
  foreignAuthModulesForSite(site).includes(foreignAuthModule);

function bitpackClaimArray(claimSet, authMod = authModules.VCCM_AUTH_MODULE) {
  const claimList = authModuleClaims[authMod];
  // bit-packed data chunks
  // Using 16 bits per integer to avoid setting sign bit
  const bigInt = new Array(Math.ceil(claimList.length / 16)).fill(0);

  for (const claim of claimSet) {
    const claims_idx = claimList.findIndex((c) => c === claim);
    if (claims_idx >= 0) {
      // identify chunk
      const bigInt_idx = Math.floor(claims_idx / 16);
      let shortInt = bigInt[bigInt_idx];
      // index relative to this chunk
      const rel_idx = claims_idx - bigInt_idx * 16;
      // shift a bit corresponding to claim index
      const flag = 1 << rel_idx;
      // set bit
      shortInt |= flag;
      bigInt[bigInt_idx] = shortInt;
    } // else claim not found
  }
  // order most significant first, join hex string
  return bigInt
    .reverse()
    .map((i) => i.toString(16).padStart(4, "0"))
    .join("")
    .replace(/^0+/, ""); // trim leading zeroes
}

function unpackClaimHex(hexStr, authMod = authModules.VCCM_AUTH_MODULE) {
  const claimList = authModuleClaims[authMod];
  const claimSet = [];

  // Segmenting hex into 16-bit integers to avoid shift overflow
  const bigInt = [];
  for (let i = 0; i > -hexStr.length; i -= 4) {
    const shortInt = Number.parseInt(
      hexStr.slice(i - 4, hexStr.length + i),
      16
    );
    if (Number.isNaN(shortInt)) {
      return null;
    }
    bigInt.push(shortInt);
  }

  for (
    let claims_idx = 0;
    claims_idx < claimList.length && claims_idx < bigInt.length * 16;
    ++claims_idx
  ) {
    // identify chunk
    const bigInt_idx = Math.floor(claims_idx / 16);
    // index relative to this chunk
    const rel_idx = claims_idx - bigInt_idx * 16;
    if ((1 << rel_idx) & bigInt[bigInt_idx])
      claimSet.push(claimList[claims_idx]);
  }
  return claimSet;
}

export function packClaimEntry(authMod, claimList) {
  if (claimList instanceof Array) return bitpackClaimArray(claimList, authMod);
  else return !!claimList;
}

export function unpackClaimEntry(siteId, authMod, claimPack) {
  let retval;
  if (typeof claimPack === "string") {
    const claimArray = unpackClaimHex(claimPack, authMod);
    if (claimArray == null) {
      const err = new Error(
        `Failed to parse "${claimPack}" into a claim set for site ${siteId}, module ${authMod}`
      );
      Object.assign(err, { siteId, claimString: claimPack });
      retval = err;
    } else {
      retval = claimArray;
    }
  } else {
    retval = !!claimPack;
  }
  return retval;
}

/**
 * Pack map of claim arrays into a map of claim hex strings
 * @param claimsMap object mapping site ID string to array of claim strings
 * @param authMod auth module name
 * @returns object mapping site ID string to hex string of packed claims
 */
export function packClaimsForModule(
  claimsMap,
  authMod = authModules.VCCM_AUTH_MODULE
) {
  return Object.entries(claimsMap).reduce((packed, [siteId, claimList]) => {
    packed[siteId] = packClaimEntry(authMod, claimList);
    return packed;
  }, {});
}

/**
 * Pack site to named claims mapping into a string
 * @param claimsMap object mapping site ID string to array of claim strings
 * @param authMod auth module name
 * @returns {string} serialized JSON map of site ID string to hex string of bit packed number
 */
export function serializeClaimsForModule(
  claimsMap,
  authMod = authModules.VCCM_AUTH_MODULE
) {
  return JSON.stringify(packClaimsForModule(claimsMap, authMod));
}

/**
 * Pack map of claim arrays into a map of claim hex strings
 * @param claimsMap object mapping site ID string -> auth module string -> array of claim strings
 * @returns {{}} object map of site ID string -> auth module string -> hex string of bit packed number
 */
export function packClaims(claimsMap) {
  return Object.entries(claimsMap).reduce((packed, [siteId, siteClaims]) => {
    packed[siteId] = Object.entries(siteClaims).reduce(
      (sitePacked, [aMod, claimList]) => {
        sitePacked[aMod] = packClaimEntry(aMod, claimList);
        return sitePacked;
      },
      {}
    );
    return packed;
  }, {});
}

/**
 * Pack site to named claims mapping into a string
 * @param claimsMap object mapping site ID string -> auth module string -> array of claim strings
 * @returns {string} serialized JSON map of site ID string -> auth module string -> hex string of bit packed number
 */
export function serializeClaims(claimsMap) {
  return JSON.stringify(packClaims(claimsMap));
}

/**
 * Unpack map of claim hex strings into site to named claims mapping
 * @param packedMap object map of site ID string to hex string of bit packed number
 * @param authMod auth module name
 * @returns {{}} object mapping site ID string to array of claim strings and errors
 */
export function unpackClaimsForModule(
  packedMap,
  authMod = authModules.VCCM_AUTH_MODULE
) {
  return Object.entries(packedMap).reduce((unpacked, [siteId, claimPack]) => {
    unpacked[siteId] = unpackClaimEntry(siteId, authMod, claimPack);
    return unpacked;
  }, {});
}

/**
 * Unpack string into site to named claims mapping
 * @param packedString serialized JSON map of site ID string to hex string of bit packed number
 * @param authMod auth module name
 * @returns {{}} object mapping site ID string to array of claim strings and errors
 * @throws JSON parse error
 */
export function deserializeClaimsForModule(
  packedString,
  authMod = authModules.VCCM_AUTH_MODULE
) {
  const packedMap = JSON.parse(packedString);
  return unpackClaimsForModule(packedMap, authMod);
}

/**
 * Unpack map of claim hex strings into named claims mapping
 * @param packedMap object map of site ID string -> auth module string -> hex string of bit packed number
 * @param authMod auth module name
 * @returns {{}} object mapping site ID string -> auth module string -> array of claim strings and errors
 */
export function unpackClaims(packedMap) {
  return Object.entries(packedMap).reduce((unpacked, [siteId, siteClaims]) => {
    unpacked[siteId] = Object.entries(siteClaims).reduce(
      (siteUnpacked, [aMod, claimPack]) => {
        siteUnpacked[aMod] = unpackClaimEntry(siteId, aMod, claimPack);
        return siteUnpacked;
      },
      {}
    );
    return unpacked;
  }, {});
}

/**
 * Unpack string into site -> module -> named claims mapping
 * @param packedString serialized JSON map of site ID string -> auth module string -> hex string of bit packed number
 * @returns {{}} object mapping site ID string -> auth module string -> array of claim strings and errors
 * @throws JSON parse error
 */
export function deserializeClaims(packedString) {
  const packedMap = JSON.parse(packedString);
  return unpackClaims(packedMap);
}

export const licensedModuleInfo = {
  design: { name: "Design" },
  inspect: { name: "Inspect" },
  intelligence: { name: "Intelligence" },
  orderexe: { name: "Order Execution" },
  beta: { name: "Beta" },
};

export function moduleLicensed(site, moduleInfo) {
  return Array.from(
    (site && site.modules && site.modules.values) || []
  ).includes(moduleInfo && moduleInfo.name);
}

export function createVrsRole(role, siteId) {
  return `${siteForeignModuleAuthMap.vrs}-${role}-${siteId}`;
}

export function createApiRole(role, siteId) {
  return `${siteForeignModuleAuthMap.api}-${role}-${siteId}`;
}

export function createVrsOperationRole(role, siteId) {
  return `${siteForeignModuleAuthMap.vrsOperations}-${role}-${siteId}`;
}

export function createVrsReportRole(role, siteId) {
  return `${siteForeignModuleAuthMap.vrsReporting}-${role}-${siteId}`;
}

export function createVrsSuperUserRole(role, siteId) {
  return `${siteForeignModuleAuthMap.vrsSuperUser}-${role}-${siteId}`;
}

function upperFirstLetter(value) {
  return value.replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

export function roleFactory(authMod, organisationUnit) {
  switch (authMod) {
    case siteForeignModuleAuthMap.vrs:
      return vrsRoles.site.map((role) => ({
        id: createVrsRole(role, organisationUnit),
        readonly: true,
        siteId: organisationUnit,
        title: upperFirstLetter(role),
        authModule: siteForeignModuleAuthMap.vrs,
        claimIds: [role],
      }));
    case siteForeignModuleAuthMap.api:
      return apiRoles.site
        .map((role) => ({
          id: createApiRole(role, organisationUnit),
          readonly: true,
          siteId: organisationUnit,
          title: `Api ${role
            .split("-")
            .map((word) => upperFirstLetter(word))
            .join(" ")}`,
          authModule: siteForeignModuleAuthMap.api,
          description: "This role allows full access to api",
          claimIds: [role],
        }))
        .concat([
          {
            id: createApiRole(apiFieldServiceAccess, rootId),
            readonly: true,
            siteId: rootId,
            title: `Field Service Access *`,
            authModule: siteForeignModuleAuthMap.api,
            description: "This role allows full access to api",
            claimIds: [apiFieldServiceAccess],
          },
        ]);
    case siteForeignModuleAuthMap.vrsOperations:
      return vrsOperationsRoles.site.map((role) => ({
        id: createVrsOperationRole(role, organisationUnit),
        readonly: true,
        siteId: organisationUnit,
        title: `Vrs Operations ${role.split("-").join(" ")}`,
        authModule: siteForeignModuleAuthMap.vrsOperations,
        description: "This role allows access to vrs operations",
        claimIds: [role],
      }));
    case siteForeignModuleAuthMap.vrsReporting:
      return authModuleClaims.vrsReporting.map((role) => ({
        id: createVrsReportRole(role, organisationUnit),
        readonly: true,
        siteId: organisationUnit,
        title: `Vrs Report ${role.split("-").join(" ")}`,
        authModule: siteForeignModuleAuthMap.vrsReporting,
        description: "This role allows access to vrs reports",
        claimIds: [role],
      }));
    case siteForeignModuleAuthMap.vrsSuperUser:
      return authModuleClaims.vrsSuperUser.map((role) => ({
        id: createVrsSuperUserRole(role, organisationUnit),
        readonly: true,
        siteId: organisationUnit,
        title: `Vrs SuperUser ${role.split("-").join(" ")}`,
        authModule: siteForeignModuleAuthMap.vrsSuperUser,
        description: "This role allows access to vrs super user",
        claimIds: [role],
      }));
    default:
      return {};
  }
}

export function userRoleValidator(authMod, roles, params) {
  switch (authMod) {
    case authModules.VRS_AUTH_MODULE:
    case authModules.VRS_OPERATIONS_AUTH_MODULE: {
      const roleIds = roleFactory(authMod, params.siteId).map((r) => r.id);
      return (
        roles.length === 0 ||
        (roles.length === 1 && roles.every((role) => roleIds.includes(role)))
      );
    }
    case authModules.API_AUTH_MODULE:
    case authModules.VRS_REPORTING_AUTH_MODULE: {
      const roleIds = roleFactory(authMod, params.siteId).map((r) => r.id);
      return (
        roles.length === 0 || roles.every((role) => roleIds.includes(role))
      );
    }
    default:
      return (
        roles.length === 0 || roles.every((role) => typeof role === "string")
      );
  }
}
